<template>
	<div
		class="c-GroupedInputText"
		:class="groupedInputTextObjClass">
		<Select
			class="c-GroupedInputText__select"
			:label="selectLabel"
			:select-items="selectItems"
			:data="selectData"
			:with-clear="false"
			:disabled="disabled"
			:style="selectStyleObj"
			:is-active="isActive"
			@change="handleSelectChange"></Select>
		<InputText
			class="c-GroupedInputText__inputText"
			:label="inputTextLabel"
			:data="inputData"
			:disabled="disabled"
			:is-active="isActive"
			@change="handleInputTextChange"></InputText>
	</div>
</template>

<script>
import Select from '../Select/Select.vue'
import InputText from '../InputText/InputText.vue'

export default {
	name: 'GroupedInputText',
	components: {
		Select,
		InputText
	},
	props: {
		/**
     * The label for the Select part
     */
		selectLabel: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The options for the Select's dropdown
     */
		selectItems: {
			type: Array,
			required: true,
			default: () => [],
			validator: (data) => {
				const conditions = []
				data.forEach((item) => {
					conditions.push(Object.keys(item).indexOf('label'))
					conditions.push(Object.keys(item).indexOf('value'))
				})

				return data.length === 0 || conditions.every(cond => cond > -1)
			}
		},
		/**
     * The GroupedInputText Select width. Defaults to 110px CSS width
		 * @values true, false
     */
		selectWidth: {
			type: Number,
			required: false,
			default: () => undefined
		},
		/**
     * The GroupedInputText data variable
     */
		data: {
			type: Object,
			required: false,
			default: () => undefined
		},
		/**
     * The label prefix for the Input part
     */
		inputLabelPrefix: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * If the GroupedInputText is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the GroupedInputText is disabled
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			selectData: undefined,
			inputData: undefined
		}
	},
	computed: {
		groupedInputTextObjClass () {
			return {
				isActive: this.isActive
			}
		},
		inputTextLabel () {
			const option = this.selectItems.find(item => item.value === this.selectData)
			return option ? `${this.inputLabelPrefix} ${option.label}` : ''
		},
		selectStyleObj () {
			if (this.selectWidth) {
				return {
					width: `${this.selectWidth}px`
				}
			}

			return {}
		}
	},
	watch: {
		data: {
			handler (newVal) {
				if (newVal && Object.keys(newVal).length === 1) {
					const key = Object.keys(newVal)[0]
					this.selectData = key
					this.inputData = newVal[`${key}`]
				} else if (this.selectItems && this.selectItems[0] && this.selectItems[0].value) {
					this.selectData = this.selectItems[0].value
				}
			},
			immediate: true
		}
	},
	methods: {
		handleSelectChange (val) {
			this.selectData = val
			this.$emit('change', this.selectData, this.inputData)
		},
		handleInputTextChange (val) {
			this.inputData = val || undefined
			/**
			 * Triggers when GroupedInputText changes it's value
			 * @event change
			 * @property {string} selectData new value set for the select part
			 * @property {string} inputData new value set for the input part
			 */
			this.$emit('change', this.selectData, this.inputData)
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';
.c-GroupedInputText {
  $select-fixed-width: 110px;

  width: 100%;
  border-radius: $border-radius;
  display: flex;
	flex: auto;

	&.isActive .c-GroupedInputText__select::before {
		content: '';
		width: 2px;
		height: calc(100% - 2px);
		position: absolute;
		top: 1px;
		right: -1px;
		z-index: 100;
		background-color: $un-white;
	}

  &__select {
    width: $select-fixed-width;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }

  &__inputText {
    width: calc(100% - #{$select-fixed-width});
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}
</style>
